import React, { useState, useRef, useEffect } from 'react';
import { 
  MdEmail, 
  MdPhone, 
  MdAccessTime, 
  MdSend,
  MdWhatsapp,
  MdChatBubble,
  MdVerified,
  MdCheckCircle,
  MdError,
  MdOutlineSupportAgent,
  MdOutlineBusinessCenter,
  MdPerson,
  MdSubject,
  MdMessage,
  MdHeadset,
  MdSupportAgent,
  MdBusiness
} from 'react-icons/md';
import { 
  FaMapMarkerAlt, 
  FaArrowRight, 
  FaGlobe,
  FaHandshake,
  FaUsers
} from 'react-icons/fa';
import { GiGrowth } from 'react-icons/gi';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    subject: '',
    message: ''
  });

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [focusedField, setFocusedField] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [emailjsInitialized, setEmailjsInitialized] = useState(false);

  // Set page title and meta tags
  useEffect(() => {
    document.title = 'Contact Us | Krishidnya - Innovative Business Solutions';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get in touch with Krishidnya. Contact our team for business partnerships, career opportunities, investor relations, technical support, and general inquiries.');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'description';
      newMeta.content = 'Get in touch with Krishidnya. Contact our team for business partnerships, career opportunities, investor relations, technical support, and general inquiries.';
      document.head.appendChild(newMeta);
    }
    
    // Add keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      const keywordsMeta = document.createElement('meta');
      keywordsMeta.name = 'keywords';
      keywordsMeta.content = 'contact Krishidnya, business inquiry, career opportunities, investor relations, technical support, partnership inquiry';
      document.head.appendChild(keywordsMeta);
    }
    
    // Add structured data for SEO
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Krishidnya Contact Page",
      "description": "Contact page for Krishidnya Business Solutions",
      "url": window.location.href,
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-8010825179",
        "contactType": "Customer Service",
        "email": "hello@krishidnya.com",
        "areaServed": "Worldwide",
        "availableLanguage": ["English", "Hindi"]
      }
    });
    document.head.appendChild(script);
    
    return () => {
      // Clean up script on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Check viewport
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);

    return () => {
      window.removeEventListener('resize', checkViewport);
    };
  }, []);

  // Initialize EmailJS - Fixed to avoid Strict Mode warnings
  useEffect(() => {
    let mounted = true;

    const initializeEmailJS = async () => {
      try {
        if (!emailjsInitialized) {
          // Initialize EmailJS
          await emailjs.init("ZHPXVSO8qBhu1zyuu");
          if (mounted) {
            setEmailjsInitialized(true);
            console.log('EmailJS initialized successfully');
          }
        }
      } catch (error) {
        console.error('Failed to initialize EmailJS:', error);
        if (mounted) {
          setSubmitStatus({
            type: 'error',
            message: 'Contact form initialization failed. Please refresh the page.'
          });
        }
      }
    };

    initializeEmailJS();

    return () => {
      mounted = false;
    };
  }, [emailjsInitialized]);

  const services = [
    { 
      value: 'business-inquiry', 
      label: 'Business Partnership', 
      icon: <MdBusiness />,
      description: 'Collaboration opportunities'
    },
    { 
      value: 'career-opportunities', 
      label: 'Career Opportunities', 
      icon: <FaUsers />,
      description: 'Join our team'
    },
    { 
      value: 'investor-relations', 
      label: 'Investor Relations', 
      icon: <GiGrowth />,
      description: 'Investment opportunities'
    },
    { 
      value: 'media-inquiry', 
      label: 'Media Inquiry', 
      icon: <MdChatBubble />,
      description: 'Press & media requests'
    },
    { 
      value: 'technical-support', 
      label: 'Technical Support', 
      icon: <MdOutlineSupportAgent />,
      description: 'Product & service support'
    },
    { 
      value: 'general-inquiry', 
      label: 'General Inquiry', 
      icon: <MdOutlineBusinessCenter />,
      description: 'General questions'
    },
    { 
      value: 'product-inquiry', 
      label: 'Product Inquiry', 
      icon: <FaHandshake />,
      description: 'Product information'
    }
  ];

  const contactPoints = [
    {
      icon: <MdEmail />,
      title: 'Email Support',
      details: ['hello@krishidnya.com', 'support@krishidnya.com'],
      description: 'Primary communication channel',
      color: 'var(--primary-green)',
      link: 'mailto:hello@krishidnya.com'
    },
    {
      icon: <MdPhone />,
      title: 'Phone Support',
      details: ['+91 8010825179'],
      description: 'Direct consultation',
      color: 'var(--secondary-green)',
      link: 'tel:+918010825179'
    },
    {
      icon: <MdWhatsapp />,
      title: 'WhatsApp',
      details: ['+91 8010825179'],
      description: 'Quick messaging',
      color: '#25D366',
      link: 'https://wa.me/918010825179'
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Location',
      details: ['Maharashtra, India'],
      description: 'Headquarters',
      color: 'var(--accent-green)',
      link: 'https://maps.google.com'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (name === 'message') {
      setCharCount(value.length);
    }
    
    if (submitStatus.type) {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const handleFocus = (field) => setFocusedField(field);
  const handleBlur = () => setFocusedField(null);

  const validateForm = () => {
    if (!formData.name.trim()) return 'Name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Please enter a valid email address';
    if (!formData.subject.trim()) return 'Subject is required';
    if (!formData.message.trim()) return 'Message is required';
    if (formData.message.length < 20) return 'Message should be at least 20 characters';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setSubmitStatus({
        type: 'error',
        message: validationError
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (!emailjsInitialized) {
      setSubmitStatus({
        type: 'error',
        message: 'Contact form is not ready. Please try again in a moment.'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const requestId = 'CONTACT-' + Math.random().toString(36).substr(2, 9).toUpperCase();
      const timestamp = Date.now().toString().slice(-6);

      // Direct email parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        service: formData.service || 'General Inquiry',
        subject: formData.subject,
        message: formData.message,
        to_email: 'pawarrushikesh735@gmail.com',
        date: new Date().toLocaleDateString('en-IN', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        time: new Date().toLocaleTimeString('en-IN', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        }),
        request_id: requestId,
        timestamp: timestamp,
        page_url: window.location.href
      };

      console.log('Sending email with params:', templateParams);

      // Send email using EmailJS with updated configuration
      const response = await emailjs.send(
        'service_4dby9cm',
        'template_9hlmfnm',
        templateParams,
        {
          publicKey: "ZHPXVSO8qBhu1zyuu",
          blockHeadless: true, // Prevent headless browser detection
          limitRate: {
            enable: true,
            interval: 60000, // 1 minute
            throttle: 5 // 5 emails per minute
          }
        }
      );

      console.log('Email sent successfully:', response);

      if (response.status === 200 || response.status === 201) {
        setSubmitStatus({
          type: 'success',
          message: `Thank you for contacting Krishidnya! Your message has been sent successfully. Our team will respond within 24 hours. Reference ID: ${requestId}`
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          subject: '',
          message: ''
        });
        setCharCount(0);
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      
      let errorMessage = 'Unable to send your message at this time. Please try again.';
      
      // Handle specific error codes
      if (error.code === 412) {
        errorMessage = 'Email validation failed. Please check your email address and try again.';
      } else if (error.code === 400) {
        errorMessage = 'Invalid form data. Please check all fields and try again.';
      } else if (error.code === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.text?.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      setSubmitStatus({
        type: 'error',
        message: errorMessage
      });
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="universal-contact">
      {/* Hero Section */}
      <header className="contact-hero" role="banner" aria-labelledby="hero-title">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <MdVerified />
                <span>Global Business Support</span>
              </div>
              
              <h1 id="hero-title" className="hero-title">
                Connect With
                <span className="title-green">
                  Our Team
                </span>
              </h1>
              
              <p className="hero-description">
                Whether you're interested in business partnerships, career opportunities, 
                investor relations, or have general inquiries, our team is here to help. 
                Reach out to us for innovative solutions and collaborative opportunities.
              </p>
              
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-number">24h</div>
                  <div className="stat-label">Response Time</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">Global</div>
                  <div className="stat-label">Reach</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">100%</div>
                  <div className="stat-label">Commitment</div>
                </div>
              </div>
            </div>
            
            <div className="hero-visual">
              <div className="visual-card">
                <div className="visual-icon">
                  <FaHandshake />
                </div>
                <h3>Partnership Ready</h3>
                <p>Collaborate with us for innovative business solutions and growth opportunities</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="hero-bottom">
          <div className="container">
            <div className="scroll-indicator">
              <span>Get in touch below</span>
              <div className="scroll-line"></div>
            </div>
          </div>
        </div>
      </header>

      {/* Contact Form Section */}
      <section className="form-section" aria-labelledby="form-title">
        <div className="container">
          <div className="section-intro">
            <h2 id="form-title" className="section-title">
              Send Your Inquiry
            </h2>
            <p className="section-description">
              Fill out the form below for business inquiries, partnership opportunities, 
              career information, or general support. Our team will respond promptly.
            </p>
          </div>
          
          <div className="form-wrapper">
            {/* Contact Form */}
            <div className="form-card">
              <div className="form-header">
                <div className="header-icon">
                  <MdMessage />
                </div>
                <h3>Contact Form</h3>
                <p>Complete all required fields for comprehensive support</p>
              </div>
              
              {submitStatus.type && (
                <div className={`status-message ${submitStatus.type}`} role="alert">
                  <div className="status-icon">
                    {submitStatus.type === 'success' ? <MdCheckCircle /> : <MdError />}
                  </div>
                  <div className="status-content">
                    <p>{submitStatus.message}</p>
                  </div>
                </div>
              )}
              
              <form ref={formRef} className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className={`form-label ${focusedField === 'name' ? 'focused' : ''}`}>
                      <MdPerson className="label-icon" />
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => handleFocus('name')}
                      onBlur={handleBlur}
                      required
                      placeholder="Enter your full name"
                      className={`form-input ${formData.name ? 'filled' : ''}`}
                      aria-required="true"
                    />
                    <div className="input-underline"></div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email" className={`form-label ${focusedField === 'email' ? 'focused' : ''}`}>
                      <MdEmail className="label-icon" />
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => handleFocus('email')}
                      onBlur={handleBlur}
                      required
                      placeholder="your.email@example.com"
                      className={`form-input ${formData.email ? 'filled' : ''}`}
                      aria-required="true"
                    />
                    <div className="input-underline"></div>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone" className={`form-label ${focusedField === 'phone' ? 'focused' : ''}`}>
                      <MdPhone className="label-icon" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => handleFocus('phone')}
                      onBlur={handleBlur}
                      placeholder="+91 98765 43210"
                      className={`form-input ${formData.phone ? 'filled' : ''}`}
                    />
                    <div className="input-underline"></div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="service" className={`form-label ${focusedField === 'service' ? 'focused' : ''}`}>
                      <MdOutlineBusinessCenter className="label-icon" />
                      Inquiry Type
                    </label>
                    <div className="select-wrapper">
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        onFocus={() => handleFocus('service')}
                        onBlur={handleBlur}
                        className={`form-select ${formData.service ? 'filled' : ''}`}
                      >
                        <option value="">Select inquiry type</option>
                        {services.map((service, index) => (
                          <option key={index} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                      <div className="select-arrow">▼</div>
                      <div className="input-underline"></div>
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject" className={`form-label ${focusedField === 'subject' ? 'focused' : ''}`}>
                    <MdSubject className="label-icon" />
                    Subject <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onFocus={() => handleFocus('subject')}
                    onBlur={handleBlur}
                    required
                    placeholder="Brief about your inquiry"
                    className={`form-input ${formData.subject ? 'filled' : ''}`}
                    aria-required="true"
                  />
                  <div className="input-underline"></div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className={`form-label ${focusedField === 'message' ? 'focused' : ''}`}>
                    <MdMessage className="label-icon" />
                    Your Message <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => handleFocus('message')}
                    onBlur={handleBlur}
                    required
                    rows="6"
                    placeholder="Please provide details about your inquiry, questions, or requirements..."
                    className={`form-textarea ${formData.message ? 'filled' : ''}`}
                    maxLength="1000"
                    aria-required="true"
                  ></textarea>
                  <div className="input-underline"></div>
                  <div className="textarea-info">
                    <span className="char-count">
                      {charCount}/1000 characters
                    </span>
                    <span className="char-min">Minimum 20 characters</span>
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isLoading}
                  aria-label={isLoading ? "Sending your inquiry" : "Submit inquiry"}
                >
                  {isLoading ? (
                    <span className="btn-loading">
                      <span className="spinner"></span>
                      Sending Your Message...
                    </span>
                  ) : (
                    <>
                      <MdSend className="btn-icon" />
                      Send Message
                      <FaArrowRight className="btn-arrow" />
                    </>
                  )}
                </button>
                
                <div className="form-note">
                  <MdVerified className="note-icon" />
                  <p>Your information is secure with us. We respect your privacy and handle all inquiries confidentially.</p>
                </div>
                
                {/* Alternative contact methods */}
                <div className="alternative-contact">
                  <p className="alternative-text">
                    Prefer to contact us directly?
                  </p>
                  <div className="alternative-links">
                    <a href="mailto:hello@krishidnya.com" className="alternative-link">
                      Email: hello@krishidnya.com
                    </a>
                    <a href="tel:+918010825179" className="alternative-link">
                      Phone: +91 8010825179
                    </a>
                  </div>
                </div>
              </form>
            </div>
            
            {/* Contact Information Sidebar */}
            <div className="contact-sidebar">
              <div className="sidebar-card">
                <div className="card-header">
                  <div className="card-icon">
                    <MdHeadset />
                  </div>
                  <h3>Contact Information</h3>
                  <p>Multiple ways to connect with our team</p>
                </div>
                
                <div className="contact-channels">
                  {contactPoints.map((point, index) => (
                    <a 
                      key={index} 
                      href={point.link}
                      target={point.link.includes('http') ? '_blank' : '_self'}
                      rel={point.link.includes('http') ? 'noopener noreferrer' : ''}
                      className="channel-card"
                    >
                      <div className="channel-icon" style={{ background: point.color }}>
                        {point.icon}
                      </div>
                      <div className="channel-content">
                        <h4>{point.title}</h4>
                        {point.details.map((detail, idx) => (
                          <p key={idx} className="channel-detail">{detail}</p>
                        ))}
                        <p className="channel-desc">{point.description}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
              
              <div className="sidebar-card hours-card">
                <div className="card-header">
                  <div className="card-icon">
                    <MdAccessTime />
                  </div>
                  <h3>Business Hours</h3>
                  <p>When we're available</p>
                </div>
                
                <div className="hours-list">
                  <div className="hour-item">
                    <span className="day">Monday - Friday</span>
                    <div className="time-slot primary">9:00 AM - 6:00 PM IST</div>
                  </div>
                  <div className="hour-item">
                    <span className="day">Saturday</span>
                    <div className="time-slot secondary">10:00 AM - 4:00 PM IST</div>
                  </div>
                  <div className="hour-item">
                    <span className="day">Sunday</span>
                    <div className="time-slot accent">Available by appointment</div>
                  </div>
                </div>
              </div>
              
              <div className="sidebar-card quick-card">
                <div className="card-header">
                  <div className="card-icon">
                    <MdSupportAgent />
                  </div>
                  <h3>Quick Connect</h3>
                  <p>Fastest ways to reach us</p>
                </div>
                
                <div className="quick-actions">
                  <a 
                    href="mailto:hello@krishidnya.com?subject=Quick%20Inquiry" 
                    className="quick-action email"
                    aria-label="Email for quick inquiry"
                  >
                    <MdEmail className="action-icon" />
                    <div className="action-content">
                      <span className="action-title">Email Inquiry</span>
                      <span className="action-sub">Direct response</span>
                    </div>
                  </a>
                  
                  <a 
                    href="tel:+918010825179" 
                    className="quick-action phone"
                    aria-label="Call for immediate assistance"
                  >
                    <MdPhone className="action-icon" />
                    <div className="action-content">
                      <span className="action-title">Call Support</span>
                      <span className="action-sub">Direct conversation</span>
                    </div>
                  </a>
                  
                  <a 
                    href="https://wa.me/918010825179" 
                    className="quick-action whatsapp"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp for quick chat"
                  >
                    <MdWhatsapp className="action-icon" />
                    <div className="action-content">
                      <span className="action-title">WhatsApp Chat</span>
                      <span className="action-sub">Instant messaging</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="info-section" aria-labelledby="info-title">
        <div className="container">
          <div className="section-intro">
            <h2 id="info-title" className="section-title">
              Why Connect With Us
            </h2>
            <p className="section-description">
              Discover opportunities and get the support you need from our dedicated team
            </p>
          </div>
          
          <div className="info-grid">
            <div className="info-card">
              <div className="info-icon">
                <MdBusiness />
              </div>
              <h3>Business Partnerships</h3>
              <p>Explore collaboration opportunities, joint ventures, and strategic partnerships for mutual growth and innovation.</p>
            </div>
            
            <div className="info-card">
              <div className="info-icon">
                <FaUsers />
              </div>
              <h3>Career Opportunities</h3>
              <p>Join our team of innovators and professionals. We're always looking for talented individuals to grow with us.</p>
            </div>
            
            <div className="info-card">
              <div className="info-icon">
                <GiGrowth />
              </div>
              <h3>Investor Relations</h3>
              <p>Learn about investment opportunities and how you can partner with us in our growth journey.</p>
            </div>
            
            <div className="info-card">
              <div className="info-icon">
                <MdOutlineSupportAgent />
              </div>
              <h3>Technical Support</h3>
              <p>Get expert assistance with our products and services. Our technical team is ready to help you succeed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" aria-labelledby="faq-title">
        <div className="container">
          <div className="section-intro">
            <h2 id="faq-title" className="section-title">
              Frequently Asked Questions
            </h2>
            <p className="section-description">
              Quick answers to common questions about contacting us
            </p>
          </div>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How quickly will I receive a response?</h3>
              <p>We typically respond to all inquiries within 24 hours during business days. Urgent matters may receive faster responses.</p>
            </div>
            
            <div className="faq-item">
              <h3>What information should I include in my inquiry?</h3>
              <p>Please include your name, contact information, and detailed description of your inquiry or question for the best response.</p>
            </div>
            
            <div className="faq-item">
              <h3>Do you provide international support?</h3>
              <p>Yes, we support clients and partners globally. Our team is equipped to handle inquiries from around the world.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I schedule a meeting with your team?</h3>
              <p>Absolutely! Please mention your preferred meeting time in your message, and we'll coordinate a suitable time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" aria-labelledby="cta-title">
        <div className="container">
          <div className="cta-content">
            <div className="cta-badge">
              <FaGlobe />
              <span>Global Reach, Local Support</span>
            </div>
            
            <h2 id="cta-title" className="cta-title">
              Ready to Connect?
            </h2>
            
            <p className="cta-description">
              Whether you have questions, partnership ideas, career inquiries, or just want to learn more about us, 
              we're here to help. Reach out today and let's start a conversation.
            </p>
            
            <div className="cta-actions">
              <button 
                className="btn-primary"
                onClick={() => document.querySelector('.form-section').scrollIntoView({ behavior: 'smooth' })}
                aria-label="Send us a message"
              >
                <span>Send Message</span>
                <FaArrowRight className="btn-icon" />
              </button>
              
              <a 
                href="tel:+918010825179" 
                className="btn-secondary"
                aria-label="Call our support line"
              >
                <span>Call Now</span>
                <MdPhone className="btn-icon" />
              </a>
            </div>
            
            <div className="cta-info">
              <p><strong>Email:</strong> hello@krishidnya.com</p>
              <p><strong>Phone:</strong> +91 8010825179</p>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Premium Design System */
        :root {
          --primary-black: #000000;
          --secondary-black: #1A1A1A;
          --medium-black: #333333;
          --light-black: #666666;
          --primary-green: #2E8B57;
          --secondary-green: #3A7D34;
          --light-green: #4CAF7A;
          --accent-green: #2D5A27;
          --white: #FFFFFF;
          --off-white: #FAFAFA;
          --light-gray: #F5F5F5;
          --medium-gray: #E8E8E8;
          --border-color: #E0E0E0;
          --text-primary: #000000;
          --text-secondary: #333333;
          --text-light: #666666;
          --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
          --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
          --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
          --shadow-xl: 0 16px 40px rgba(0, 0, 0, 0.16);
          --radius-sm: 6px;
          --radius-md: 10px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          --radius-2xl: 30px;
          --transition: all 0.3s ease;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .universal-contact {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: var(--text-primary);
          background: var(--white);
          line-height: 1.6;
          overflow-x: hidden;
          scroll-behavior: smooth;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 ${isMobile ? '20px' : isTablet ? '32px' : '40px'};
        }

        /* Typography */
        h1, h2, h3, h4 {
          font-family: 'Source Serif Pro', serif;
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: -0.01em;
          color: var(--primary-black);
        }

        h1 { 
          font-size: ${isMobile ? '2.25rem' : isTablet ? '3rem' : '3.5rem'};
          font-weight: 700;
          letter-spacing: -0.02em;
          margin-bottom: 1rem;
        }

        h2 { 
          font-size: ${isMobile ? '1.75rem' : isTablet ? '2.25rem' : '2.5rem'};
          margin-bottom: 1rem;
        }

        h3 { 
          font-size: ${isMobile ? '1.25rem' : isTablet ? '1.5rem' : '1.75rem'};
          margin-bottom: 0.75rem;
        }

        p { 
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          color: var(--text-secondary);
          line-height: 1.7;
          font-weight: 400;
        }

        .section-title {
          font-size: ${isMobile ? '1.75rem' : isTablet ? '2.25rem' : '2.5rem'};
          text-align: center;
          margin-bottom: 1rem;
        }

        .section-description {
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          color: var(--text-light);
          text-align: center;
          max-width: 700px;
          margin: 0 auto ${isMobile ? '2rem' : '3rem'};
          line-height: 1.7;
        }

        /* Buttons */
        .btn-primary, .btn-secondary, .submit-btn {
          padding: ${isMobile ? '12px 24px' : '14px 32px'};
          font-size: ${isMobile ? '0.95rem' : '1rem'};
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          border-radius: var(--radius-md);
          text-decoration: none;
          transition: var(--transition);
          border: 2px solid transparent;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          text-align: center;
          min-width: ${isMobile ? '140px' : 'auto'};
        }

        .btn-primary, .submit-btn {
          background: var(--primary-black);
          color: var(--white);
        }

        .btn-primary:hover, .btn-primary:focus, .submit-btn:hover:not(:disabled) {
          background: var(--secondary-black);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
          outline: 2px solid var(--primary-green);
        }

        .btn-secondary {
          background: transparent;
          color: var(--primary-black);
          border: 2px solid var(--primary-black);
        }

        .btn-secondary:hover, .btn-secondary:focus {
          background: var(--primary-black);
          color: var(--white);
          transform: translateY(-2px);
          outline: 2px solid var(--primary-green);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-icon, .btn-arrow {
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          transition: transform 0.3s ease;
        }

        .btn-primary:hover .btn-icon,
        .btn-primary:hover .btn-arrow,
        .submit-btn:hover:not(:disabled) .btn-arrow {
          transform: translateX(4px);
        }

        /* Hero Section */
        .contact-hero {
          padding: ${isMobile ? 'calc(80px + 40px) 0 40px' : 'calc(80px + 60px) 0 60px'};
          background: var(--white);
          position: relative;
        }

        .contact-hero::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--border-color), transparent);
        }

        .hero-content {
          display: flex;
          flex-direction: ${isMobile ? 'column' : 'row'};
          gap: ${isMobile ? '40px' : '60px'};
          align-items: center;
        }

        .hero-text {
          flex: 1;
        }

        .hero-visual {
          flex: 1;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(46, 139, 87, 0.1);
          color: var(--primary-green);
          padding: ${isMobile ? '8px 16px' : '10px 20px'};
          border-radius: 30px;
          font-size: ${isMobile ? '0.85rem' : '14px'};
          font-weight: 600;
          margin-bottom: ${isMobile ? '20px' : '30px'};
          border: 1px solid rgba(46, 139, 87, 0.2);
        }

        .hero-badge svg {
          font-size: 18px;
        }

        .hero-title {
          margin-bottom: ${isMobile ? '1rem' : '1.5rem'};
        }

        .title-green {
          color: var(--primary-green);
          display: block;
          margin-top: ${isMobile ? '0.5rem' : '0.75rem'};
        }

        .hero-description {
          margin-bottom: ${isMobile ? '2rem' : '3rem'};
          font-size: ${isMobile ? '1.063rem' : '1.125rem'};
          line-height: 1.6;
        }

        /* Hero Stats */
        .hero-stats {
          display: flex;
          gap: ${isMobile ? '1rem' : '2rem'};
          margin-top: ${isMobile ? '2rem' : '3rem'};
        }

        .stat-item {
          text-align: center;
          flex: 1;
        }

        .stat-number {
          font-size: ${isMobile ? '1.5rem' : '2rem'};
          font-weight: 700;
          color: var(--primary-green);
          margin-bottom: 0.5rem;
          font-family: 'Source Serif Pro', serif;
        }

        .stat-label {
          font-size: ${isMobile ? '0.75rem' : '0.875rem'};
          color: var(--text-light);
          font-weight: 500;
        }

        /* Visual Card */
        .visual-card {
          background: var(--off-white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '1.5rem' : '2rem'};
          text-align: center;
          transition: var(--transition);
        }

        .visual-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
        }

        .visual-icon {
          font-size: 3rem;
          color: var(--primary-green);
          margin-bottom: 1rem;
        }

        .visual-card h3 {
          margin-bottom: 0.5rem;
        }

        .visual-card p {
          font-size: 0.875rem;
          color: var(--text-light);
          line-height: 1.6;
        }

        /* Scroll Indicator */
        .scroll-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-light);
          font-size: 0.875rem;
          padding-top: 2rem;
        }

        .scroll-line {
          width: 1px;
          height: 40px;
          background: var(--border-color);
          position: relative;
          overflow: hidden;
        }

        .scroll-line::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 20px;
          background: var(--primary-green);
          animation: scroll 2s infinite;
        }

        /* Form Section */
        .form-section {
          padding: ${isMobile ? '80px 0' : '120px 0'};
          background: var(--light-gray);
        }

        .section-intro {
          margin-bottom: ${isMobile ? '2rem' : '3rem'};
        }

        /* Form Wrapper */
        .form-wrapper {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : '1fr 0.8fr'};
          gap: ${isMobile ? '2rem' : '3rem'};
          align-items: start;
        }

        /* Form Card */
        .form-card {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          padding: ${isMobile ? '1.5rem' : '2.5rem'};
          box-shadow: var(--shadow-md);
        }

        .form-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .header-icon {
          width: 60px;
          height: 60px;
          background: var(--primary-green);
          color: var(--white);
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin: 0 auto 1rem;
        }

        .form-header h3 {
          margin-bottom: 0.5rem;
        }

        .form-header p {
          font-size: 0.875rem;
          color: var(--text-light);
        }

        /* Status Message */
        .status-message {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          border-radius: var(--radius-md);
          margin-bottom: 2rem;
          animation: slideIn 0.3s ease;
        }

        .status-message.success {
          background: rgba(46, 139, 87, 0.1);
          border: 1px solid rgba(46, 139, 87, 0.2);
        }

        .status-message.error {
          background: rgba(220, 38, 38, 0.1);
          border: 1px solid rgba(220, 38, 38, 0.2);
        }

        .status-icon {
          font-size: 24px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .status-message.success .status-icon {
          color: var(--primary-green);
        }

        .status-message.error .status-icon {
          color: #DC2626;
        }

        .status-content p {
          margin: 0;
          font-size: 0.875rem;
          line-height: 1.5;
        }

        /* Alternative Contact */
        .alternative-contact {
          margin-top: 2rem;
          padding: 1rem;
          background: var(--light-gray);
          border-radius: var(--radius-md);
          border: 1px solid var(--border-color);
        }

        .alternative-text {
          font-size: 0.875rem;
          color: var(--text-light);
          margin-bottom: 0.5rem;
          text-align: center;
        }

        .alternative-links {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          align-items: center;
        }

        .alternative-link {
          font-size: 0.875rem;
          color: var(--primary-green);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s;
        }

        .alternative-link:hover {
          color: var(--accent-green);
          text-decoration: underline;
        }

        /* Form Styles */
        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : '1fr 1fr'};
          gap: 1.5rem;
        }

        .form-group {
          position: relative;
        }

        .form-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
          transition: color 0.3s;
        }

        .form-label.focused {
          color: var(--primary-green);
        }

        .label-icon {
          font-size: 16px;
          opacity: 0.7;
        }

        .required {
          color: #DC2626;
          margin-left: 2px;
        }

        .form-input, .form-select, .form-textarea {
          width: 100%;
          padding: 0.875rem;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          font-family: 'Inter', sans-serif;
          font-size: 0.875rem;
          background: var(--white);
          color: var(--text-primary);
          transition: var(--transition);
        }

        .form-input:focus, .form-select:focus, .form-textarea:focus {
          outline: none;
          border-color: var(--primary-green);
          box-shadow: 0 0 0 3px rgba(46, 139, 87, 0.1);
        }

        .form-input.filled, .form-select.filled, .form-textarea.filled {
          border-color: var(--secondary-green);
        }

        .input-underline {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: var(--primary-green);
          transform: scaleX(0);
          transition: transform 0.3s;
        }

        .form-input:focus ~ .input-underline,
        .form-select:focus ~ .input-underline,
        .form-textarea:focus ~ .input-underline {
          transform: scaleX(1);
        }

        /* Select Styling */
        .select-wrapper {
          position: relative;
        }

        .form-select {
          appearance: none;
          cursor: pointer;
          padding-right: 40px;
        }

        .select-arrow {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-light);
          pointer-events: none;
          font-size: 12px;
        }

        /* Textarea */
        .form-textarea {
          resize: vertical;
          min-height: 120px;
          max-height: 300px;
        }

        .textarea-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: var(--text-light);
        }

        .char-min {
          font-style: italic;
        }

        /* Submit Button */
        .submit-btn {
          margin-top: 1rem;
          padding: 1rem;
          font-size: 1rem;
        }

        .btn-loading {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 0.8s linear infinite;
        }

        /* Form Note */
        .form-note {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: var(--light-gray);
          border-radius: var(--radius-md);
          margin-top: 1rem;
        }

        .note-icon {
          font-size: 20px;
          color: var(--primary-green);
          flex-shrink: 0;
        }

        .form-note p {
          margin: 0;
          font-size: 0.75rem;
          color: var(--text-light);
        }

        /* Sidebar */
        .contact-sidebar {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .sidebar-card {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '1.25rem' : '1.5rem'};
          box-shadow: var(--shadow-sm);
        }

        .card-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .card-icon {
          width: 48px;
          height: 48px;
          background: var(--primary-green);
          color: var(--white);
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin: 0 auto 1rem;
        }

        .card-header h3 {
          margin-bottom: 0.25rem;
        }

        .card-header p {
          font-size: 0.75rem;
          color: var(--text-light);
          margin: 0;
        }

        /* Contact Channels */
        .contact-channels {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .channel-card {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 0.75rem;
          border-radius: var(--radius-sm);
          transition: var(--transition);
          text-decoration: none;
          color: inherit;
        }

        .channel-card:hover {
          background: var(--light-gray);
          transform: translateX(4px);
        }

        .channel-icon {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: white;
          flex-shrink: 0;
        }

        .channel-content {
          flex: 1;
        }

        .channel-content h4 {
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
          color: var(--text-primary);
        }

        .channel-detail {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin: 0.125rem 0;
        }

        .channel-desc {
          font-size: 0.75rem;
          color: var(--text-light);
          margin: 0.25rem 0 0;
        }

        /* Hours List */
        .hours-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .hour-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
          border-bottom: 1px solid var(--border-color);
        }

        .hour-item:last-child {
          border-bottom: none;
        }

        .day {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .time-slot {
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          font-weight: 600;
        }

        .time-slot.primary {
          background: rgba(46, 139, 87, 0.1);
          color: var(--primary-green);
        }

        .time-slot.secondary {
          background: rgba(245, 158, 11, 0.1);
          color: #F59E0B;
        }

        .time-slot.accent {
          background: rgba(59, 130, 246, 0.1);
          color: #3B82F6;
        }

        /* Quick Actions */
        .quick-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .quick-action {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          border-radius: var(--radius-sm);
          text-decoration: none;
          color: var(--text-primary);
          transition: var(--transition);
          border: 1px solid transparent;
        }

        .quick-action:hover {
          background: var(--light-gray);
          border-color: var(--border-color);
          transform: translateX(4px);
        }

        .quick-action.whatsapp:hover {
          background: rgba(37, 211, 102, 0.1);
          border-color: rgba(37, 211, 102, 0.2);
        }

        .quick-action.phone:hover {
          background: rgba(46, 139, 87, 0.1);
          border-color: rgba(46, 139, 87, 0.2);
        }

        .quick-action.email:hover {
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.2);
        }

        .action-icon {
          font-size: 24px;
          color: var(--primary-green);
        }

        .quick-action.whatsapp .action-icon {
          color: #25D366;
        }

        .quick-action.phone .action-icon {
          color: var(--primary-green);
        }

        .quick-action.email .action-icon {
          color: #3B82F6;
        }

        .action-content {
          flex: 1;
        }

        .action-title {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 0.125rem;
        }

        .action-sub {
          display: block;
          font-size: 0.75rem;
          color: var(--text-light);
        }

        /* Info Section */
        .info-section {
          padding: ${isMobile ? '80px 0' : '100px 0'};
          background: var(--white);
        }

        .info-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'};
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .info-card {
          background: var(--off-white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: 2rem;
          text-align: center;
          transition: var(--transition);
        }

        .info-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .info-icon {
          font-size: 2.5rem;
          color: var(--primary-green);
          margin-bottom: 1rem;
        }

        .info-card h3 {
          margin-bottom: 1rem;
        }

        .info-card p {
          font-size: 0.875rem;
          color: var(--text-light);
          line-height: 1.6;
          margin: 0;
        }

        /* FAQ Section */
        .faq-section {
          padding: ${isMobile ? '80px 0' : '100px 0'};
          background: var(--light-gray);
        }

        .faq-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'};
          gap: 1.5rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .faq-item {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          transition: var(--transition);
        }

        .faq-item:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-sm);
        }

        .faq-item h3 {
          font-size: 1.125rem;
          margin-bottom: 0.75rem;
          color: var(--primary-green);
        }

        .faq-item p {
          font-size: 0.875rem;
          color: var(--text-light);
          margin: 0;
          line-height: 1.6;
        }

        /* CTA Section */
        .cta-section {
          padding: ${isMobile ? '80px 0' : '100px 0'};
          background: var(--white);
          border-top: 1px solid var(--border-color);
        }

        .cta-content {
          text-align: center;
          max-width: 700px;
          margin: 0 auto;
        }

        .cta-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(46, 139, 87, 0.1);
          color: var(--primary-green);
          padding: 0.5rem 1rem;
          border-radius: 30px;
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
        }

        .cta-title {
          margin-bottom: 1rem;
        }

        .cta-description {
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          color: var(--text-secondary);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .cta-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 2rem;
          flex-direction: ${isMobile ? 'column' : 'row'};
          align-items: ${isMobile ? 'stretch' : 'center'};
        }

        .cta-actions .btn-primary,
        .cta-actions .btn-secondary {
          min-width: ${isMobile ? '100%' : '200px'};
        }

        .cta-info {
          display: flex;
          justify-content: center;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .cta-info p {
          font-size: 0.875rem;
          color: var(--text-light);
          margin: 0;
        }

        .cta-info strong {
          color: var(--primary-black);
        }

        /* Animations */
        @keyframes scroll {
          0% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(20px);
            opacity: 0;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero-stats {
            flex-direction: column;
            gap: 1.5rem;
          }

          .form-wrapper {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .info-grid,
          .faq-grid {
            grid-template-columns: 1fr;
          }

          .cta-actions {
            flex-direction: column;
          }

          .cta-actions .btn-primary,
          .cta-actions .btn-secondary {
            width: 100%;
            max-width: 300px;
            margin: 0 auto;
          }

          .cta-info {
            flex-direction: column;
            gap: 0.5rem;
            align-items: center;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .hero-content {
            flex-direction: column;
          }

          .hero-visual {
            width: 100%;
            max-width: 500px;
            margin: 0 auto;
          }
        }

        @media (min-width: 1025px) {
          .info-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }

          .scroll-line::after {
            animation: none;
          }
        }

        /* Print Styles */
        @media print {
          .contact-hero,
          .faq-section,
          .cta-section {
            page-break-inside: avoid;
          }

          .contact-form,
          .contact-sidebar {
            page-break-inside: avoid;
          }

          .btn-primary,
          .btn-secondary,
          .submit-btn {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;
